import { Suspense, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function CareersPage() {
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  const openPositions = [
    {
      id: 1,
      title: 'Security Systems Installer',
      department: 'Installation',
      location: 'San Francisco, CA',
      type: 'Full-time',
      description: 'Install and configure access control, surveillance, and intercom systems for residential and commercial clients.',
      requirements: [
        '2+ years experience in security system installation',
        'Valid California driver\'s license',
        'Ability to work at heights and lift 50+ lbs',
        'Strong problem-solving skills',
        'Excellent customer service'
      ]
    },
    {
      id: 2,
      title: 'Security Technician',
      department: 'Technical',
      location: 'San Francisco, CA',
      type: 'Full-time',
      description: 'Provide technical support, maintenance, and troubleshooting for security systems across the Bay Area.',
      requirements: [
        '3+ years experience with IP surveillance systems',
        'Knowledge of access control and intercom systems',
        'Network troubleshooting skills',
        'Valid California driver\'s license',
        'Professional communication skills'
      ]
    },
    {
      id: 3,
      title: 'Project Manager',
      department: 'Management',
      location: 'San Francisco, CA',
      type: 'Full-time',
      description: 'Oversee security system installation projects from planning to completion, ensuring quality and client satisfaction.',
      requirements: [
        '5+ years experience in security or construction project management',
        'Strong organizational and leadership skills',
        'Experience managing multiple projects simultaneously',
        'Excellent communication and client relations',
        'PMP certification preferred'
      ]
    },
    {
      id: 4,
      title: 'Sales Consultant',
      department: 'Sales',
      location: 'San Francisco, CA',
      type: 'Full-time',
      description: 'Consult with clients to understand their security needs and design customized solutions for their properties.',
      requirements: [
        '2+ years experience in B2B or security sales',
        'Strong technical understanding of security systems',
        'Excellent presentation and negotiation skills',
        'Self-motivated with proven sales track record',
        'Valid California driver\'s license'
      ]
    },
    {
      id: 5,
      title: 'Customer Support Specialist',
      department: 'Support',
      location: 'San Francisco, CA',
      type: 'Full-time',
      description: 'Provide exceptional customer support, handle service requests, and coordinate with technical teams.',
      requirements: [
        '1+ years experience in customer service',
        'Strong communication and problem-solving skills',
        'Basic technical knowledge preferred',
        'Proficiency with CRM systems',
        'Positive attitude and team player'
      ]
    },
    {
      id: 6,
      title: 'Network Security Engineer',
      department: 'Technical',
      location: 'San Francisco, CA',
      type: 'Full-time',
      description: 'Design and implement network infrastructure for advanced security systems and ensure cybersecurity best practices.',
      requirements: [
        '4+ years experience in network engineering',
        'Knowledge of VLANs, VPNs, and network security',
        'Experience with IP camera systems and access control',
        'Cisco or similar certifications preferred',
        'Strong analytical and troubleshooting skills'
      ]
    }
  ];

  const departments = ['all', 'Installation', 'Technical', 'Management', 'Sales', 'Support'];

  const filteredPositions = selectedDepartment === 'all' 
    ? openPositions 
    : openPositions.filter(pos => pos.department === selectedDepartment);

  const benefits = [
    {
      icon: 'ri-heart-pulse-line',
      title: 'Health Insurance',
      description: 'Comprehensive medical, dental, and vision coverage for you and your family'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Paid Time Off',
      description: 'Generous vacation days, sick leave, and paid holidays throughout the year'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Competitive Pay',
      description: 'Industry-leading salaries with performance bonuses and commission opportunities'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Training & Development',
      description: 'Ongoing training, certifications, and professional development programs'
    },
    {
      icon: 'ri-car-line',
      title: 'Company Vehicle',
      description: 'Company vehicles provided for field technicians and installers'
    },
    {
      icon: 'ri-team-line',
      title: 'Team Culture',
      description: 'Collaborative environment with team events and recognition programs'
    }
  ];

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="bg-white min-h-screen">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center">
              <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-6">
                Join Our Team
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                Build your career with the Bay Area's leading security systems provider
              </p>
            </div>
          </div>
        </section>

        {/* Why Work With Us */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-coral-50 rounded-full mb-6">
                <span className="text-coral-600 font-semibold text-sm">WHY SECURITY BAY AREA</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Why Work With Us
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                We invest in our team because we know that great people deliver great results. Join a company that values your growth and success.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-slate-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mb-6">
                    <i className={`${benefit.icon} text-3xl text-coral-600`}></i>
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-2 bg-coral-50 rounded-full mb-6">
                <span className="text-coral-600 font-semibold text-sm">OPEN POSITIONS</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Current Opportunities
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
                Explore our open positions and find the perfect role for your skills and career goals.
              </p>

              {/* Department Filter */}
              <div className="flex flex-wrap justify-center gap-3">
                {departments.map((dept) => (
                  <button
                    key={dept}
                    onClick={() => setSelectedDepartment(dept)}
                    className={`px-6 py-2 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      selectedDepartment === dept
                        ? 'bg-slate-800 text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {dept === 'all' ? 'All Positions' : dept}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              {filteredPositions.map((position) => (
                <div key={position.id} className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
                    <div className="mb-4 md:mb-0">
                      <h3 className="font-serif text-2xl font-bold text-slate-900 mb-2">
                        {position.title}
                      </h3>
                      <div className="flex flex-wrap gap-3">
                        <span className="inline-flex items-center px-3 py-1 bg-coral-50 text-coral-600 rounded-full text-sm font-semibold">
                          <i className="ri-briefcase-line mr-1"></i>
                          {position.department}
                        </span>
                        <span className="inline-flex items-center px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm font-semibold">
                          <i className="ri-map-pin-line mr-1"></i>
                          {position.location}
                        </span>
                        <span className="inline-flex items-center px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm font-semibold">
                          <i className="ri-time-line mr-1"></i>
                          {position.type}
                        </span>
                      </div>
                    </div>
                    <a
                      href="/apply"
                      className="inline-block bg-coral-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-coral-600 transition-colors text-sm whitespace-nowrap cursor-pointer"
                    >
                      Apply Now
                    </a>
                  </div>

                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {position.description}
                  </p>

                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Requirements:</h4>
                    <ul className="space-y-2">
                      {position.requirements.map((req, index) => (
                        <li key={index} className="flex items-start text-slate-600">
                          <i className="ri-check-line text-coral-600 mr-2 mt-1 flex-shrink-0"></i>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Process */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-coral-50 rounded-full mb-6">
                <span className="text-coral-600 font-semibold text-sm">APPLICATION PROCESS</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                How to Apply
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Our hiring process is designed to be straightforward and respectful of your time.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl font-bold text-coral-600">1</span>
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">
                  Submit Application
                </h3>
                <p className="text-slate-600">
                  Click "Apply Now" and send us your resume and cover letter through our contact form.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl font-bold text-coral-600">2</span>
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">
                  Initial Review
                </h3>
                <p className="text-slate-600">
                  Our team reviews your application and reaches out within 5 business days.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl font-bold text-coral-600">3</span>
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">
                  Interview
                </h3>
                <p className="text-slate-600">
                  Meet with our team to discuss your experience, skills, and career goals.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl font-bold text-coral-600">4</span>
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">
                  Join the Team
                </h3>
                <p className="text-slate-600">
                  Receive your offer and start your journey with Security Bay Area!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
              Don't See the Right Position?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              We're always looking for talented individuals to join our team. Send us your resume and let us know how you can contribute to Security Bay Area.
            </p>
            <a
              href="/contact-us"
              className="inline-flex items-center px-8 py-4 bg-white text-slate-900 rounded-full font-semibold text-lg hover:bg-slate-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Contact Us
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}
